import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import { accessService } from '../../../services/central-api/access';
import { CalendarValue } from '../../../components/DateTimePicker';
import moment from 'moment';
import FilterDatePicker from '../../../components/Filter/components/FilterDateInput';
import { ScrollView } from 'react-native-web-hover';
import Toast from 'react-native-toast-message';
import { AlreadyExists } from '../../../services/central-api/base-service';
import WorkScheduleTypeDropdown from '../../../components/access/WorkScheduleTypeDropdown';
import { useAuth } from '../../../context/Auth';
import WarningModal from '../../../components/WarningModal';

export default function WorkSchedulesModal({ onClose, workScheduleId, isModalVisible }: { onClose: () => void; workScheduleId?: number; isModalVisible: boolean; }) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const [name, setName] = useState('');
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [startReferenceDate, setStartReferenceDate] = useState<CalendarValue>(null);
    const [startReferenceHour, setStartReferenceHour] = useState('');
    const [startReferenceDateZIndex, setStartReferenceDateZIndex] = useState<number>(2);
    const [workScheduleType, setWorkScheduleType] = useState<WorkScheduleType | null>(null);

    const [endReferenceDate, setEndReferenceDate] = useState<CalendarValue>(null);
    const [endReferenceHour, setEndReferenceHour] = useState('');
    const [endReferenceDateZIndex, setEndReferenceDateZIndex] = useState<number>(1);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(workScheduleId && workScheduleId > 0 ? true : false);

    const [originalStartReferenceDate, setOriginalStartReferenceDate] = useState<CalendarValue>(null);
    const [originalStartReferenceHour, setOriginalStartReferenceHour] = useState('');
    const [originalEndReferenceDate, setOriginalEndReferenceDate] = useState<CalendarValue>(null);
    const [originalEndReferenceHour, setOriginalEndReferenceHour] = useState('');
    const [originalWorkScheduleType, setOriginalWorkScheduleType] = useState<WorkScheduleType | null>(null);

    const [displayWarningModal, setDisplayWarningModal] = useState<boolean>(false);
    const [changeForWarningModal, setChangeForWarningModal] = useState<string[]>([]);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        if (name == '' ||
            actingBodyId == 0 ||
            !startReferenceDate ||
            startReferenceHour == '' ||
            !endReferenceDate ||
            endReferenceHour == '' ||
            !workScheduleType
        ) {
            return false;
        }

        return true;
    }

    function clearValues() {
        setName('');
        setActingBodyId(0);
        setStartReferenceDate(null);
        setStartReferenceHour('');
        setEndReferenceDate(null);
        setEndReferenceHour('');
        setWorkScheduleType(null);
        setOriginalStartReferenceDate(null);
        setOriginalStartReferenceHour('');
        setOriginalEndReferenceDate(null);
        setOriginalEndReferenceHour('');
        setOriginalWorkScheduleType(null);
    }

    async function getWorkSchedule() {
        try {
            setIsFetchingData(true);
            const workSchedule = await accessService.getWorkScheduleById(Number(workScheduleId));
            setName(workSchedule.name);
            setActingBodyId(workSchedule.actingBody.id);
            setWorkScheduleType(workSchedule.workScheduleType);
            setOriginalWorkScheduleType(workSchedule.workScheduleType);

            setOriginalStartReferenceDate(moment(workSchedule.startReferenceDate).toDate());
            setOriginalStartReferenceHour(moment(workSchedule.startReferenceDate).format('HH:mm'));
            setStartReferenceDate(moment(workSchedule.startReferenceDate).toDate());
            setStartReferenceHour(moment(workSchedule.startReferenceDate).format('HH:mm'));

            setOriginalEndReferenceDate(moment(workSchedule.endReferenceDate).toDate());
            setOriginalEndReferenceHour(moment(workSchedule.endReferenceDate).format('HH:mm'));
            setEndReferenceDate(moment(workSchedule.endReferenceDate).toDate());
            setEndReferenceHour(moment(workSchedule.endReferenceDate).format('HH:mm'));
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            clearValues();
            onClose();
        } finally {
            setIsFetchingData(false);
        }
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (workScheduleId) {
            getWorkSchedule();
        } else if (!(authenticatedUser.permissions.access_manage_all_acting_bodies_work_schedules) && authenticatedUser.actingBodyUnit.actingBodyId) {
            setActingBodyId(authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [workScheduleId, isModalVisible]);

    function setEndDateFromStart() {
        if (startReferenceDate && startReferenceHour != '') {
            const start = moment(`${moment(startReferenceDate).format('DD/MM/YYYY')} ${startReferenceHour}`, 'DD/MM/YYYY HH:mm');
            if (workScheduleType === '12/36') {
                const end = start.clone().add(12, 'hours');
                setEndReferenceDate(moment(end).toDate());
                setEndReferenceHour(moment(end).format('HH:mm'));
            }
        }
    }

    useEffect(() => {
        if (workScheduleType) {
            if (workScheduleType === '12/36') {
                setEndDateFromStart();
            }
        }

    }, [workScheduleType, startReferenceDate, startReferenceHour]);

    async function validateFormSubmit() {
        if (workScheduleId) {
            let displayWarningModal = false;
            const changes = [];

            const localStartReferenceDate = moment(`${moment(startReferenceDate).format('DD/MM/YYYY')} ${startReferenceHour}`, 'DD/MM/YYYY HH:mm');
            const localOriginalStartReferenceDate = moment(`${moment(originalStartReferenceDate).format('DD/MM/YYYY')} ${originalStartReferenceHour}`, 'DD/MM/YYYY HH:mm');

            if (!localOriginalStartReferenceDate.isSame(localStartReferenceDate)) {
                displayWarningModal = true;
                changes.push(translate('startReferenceDate'));
            }

            const localEndReferenceDate = moment(`${moment(endReferenceDate).format('DD/MM/YYYY')} ${endReferenceHour}`, 'DD/MM/YYYY HH:mm');
            const localOriginalEndReferenceDate = moment(`${moment(originalEndReferenceDate).format('DD/MM/YYYY')} ${originalEndReferenceHour}`, 'DD/MM/YYYY HH:mm');
            if (!localOriginalEndReferenceDate.isSame(localEndReferenceDate)) {
                displayWarningModal = true;
                changes.push(translate('endReferenceDate'));
            }

            if (originalWorkScheduleType != workScheduleType) {
                displayWarningModal = true;
                changes.push(translate('workScheduleType'));
            }

            if (displayWarningModal) {
                setChangeForWarningModal(changes);
                setDisplayWarningModal(true);
            } else {
                await formSubmit();
            }
        } else {
            await formSubmit();
        }
    }

    async function formSubmit() {
        try {
            if (!workScheduleType) {
                return;
            }

            const localStartReferenceDate = moment(`${moment(startReferenceDate).format('DD/MM/YYYY')} ${startReferenceHour}`, 'DD/MM/YYYY HH:mm').toDate();
            const localEndReferenceDate = moment(`${moment(endReferenceDate).format('DD/MM/YYYY')} ${endReferenceHour}`, 'DD/MM/YYYY HH:mm').toDate();
            if (workScheduleId) {
                await accessService.updateWorkSchedule({
                    id: workScheduleId,
                    name: name,
                    startReferenceDate: localStartReferenceDate,
                    endReferenceDate: localEndReferenceDate,
                    workScheduleType: workScheduleType,
                });
            } else {
                await accessService.createWorkSchedule({
                    name: name,
                    startReferenceDate: localStartReferenceDate,
                    endReferenceDate: localEndReferenceDate,
                    workScheduleType: workScheduleType,
                    actingBodyId: actingBodyId
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            clearValues();
            onClose();
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('workScheduleAlreadyExist'),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '35%' }]}>
                        {isFetchingData
                            ? <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                                <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                            </View>
                            : <>
                                <ScrollView style={{ flex: 1, minWidth: 300, minHeight: 350, }} contentContainerStyle={{ flexDirection: 'column', gap: 12 }}>
                                    <ActingBodiesDropdown
                                        includeAllOption={false}
                                        value={actingBodyId}
                                        setValue={setActingBodyId}
                                        zIndex={4}
                                        disabled={!!workScheduleId || !authenticatedUser?.permissions.access_manage_all_acting_bodies_work_schedules}
                                    />

                                    <FormInput
                                        label={translate('name')}
                                        placeholder={translate('name')}
                                        value={name}
                                        onChangeText={setName}
                                        invalid={isNameInvalid}
                                    />

                                    <WorkScheduleTypeDropdown
                                        value={workScheduleType}
                                        setValue={setWorkScheduleType}
                                        zIndex={3}
                                    />

                                    <View style={{ zIndex: startReferenceDateZIndex }}>
                                        <FilterDatePicker
                                            date={startReferenceDate}
                                            onChangeDate={setStartReferenceDate}
                                            time={startReferenceHour}
                                            onChangeTime={setStartReferenceHour}
                                            label={translate('startReferenceDate')}
                                            onCalendarOpen={() => setStartReferenceDateZIndex(99999)}
                                            onCalendarClose={() => setStartReferenceDateZIndex(2)}
                                            required
                                            viewStyle={{ gap: 8, flex: 1 }}
                                        />
                                    </View>

                                    <View style={{ zIndex: endReferenceDateZIndex }}>
                                        <FilterDatePicker
                                            date={endReferenceDate}
                                            onChangeDate={setEndReferenceDate}
                                            time={endReferenceHour}
                                            onChangeTime={setEndReferenceHour}
                                            label={translate('endReferenceDate')}
                                            onCalendarOpen={() => setEndReferenceDateZIndex(99999)}
                                            onCalendarClose={() => setEndReferenceDateZIndex(1)}
                                            required
                                            viewStyle={{ gap: 8, flex: 1 }}
                                            disabled={workScheduleType !== '5/2'}
                                        />
                                    </View>
                                </ScrollView>

                                <FormActions
                                    onSubmit={validateFormSubmit}
                                    onClose={() => {
                                        onClose();
                                        clearValues();
                                    }}
                                    disabled={!isFormValid()}
                                />
                            </>
                        }
                    </View>
                </View>
            </View>
            <WarningModal
                isModalVisible={displayWarningModal}
                setIsModalVisible={setDisplayWarningModal}
                text={translate('changingWorkSchedule')}
                items={changeForWarningModal}
                onSubmit={() => {
                    setDisplayWarningModal(false);
                    formSubmit();
                }}
                onClose={() => setDisplayWarningModal(false)}
            />
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
}));
